/**
 * PlaybackBar — play/pause, rate, chapter, time left, and the stall state.
 *
 * design.md §5.5: *"`PlaybackBar`: play/pause, rate, chapter, time left, and the **'warming up —
 * about 20 seconds'** stall state when a page is generating. Honest, not a spinner."*
 *
 * ── The stall state is the reason this component exists ─────────────────────
 * A page that is not narrated yet takes ~20–30 seconds of GPU, once, and then never again. Every
 * product instinct says "put a spinner there". A spinner says *something is happening* and refuses
 * to say what or for how long, so a person taps it, taps it again, and reloads — and on this product
 * the reload is worse than the wait, because it can cost the queue position that was already
 * running. The number is knowable: `GPU_CHARS_PER_SEC` is measured, the plan knows how many
 * characters are missing, and `estimateGpuMs` turns one into the other. So we say the number.
 *
 * ⚠️ The seconds come from `shared/narration/rates.ts` and **never from a literal in this file.**
 * §2.4 exists because four design documents carried four values for the speech constant and the
 * stale one shipped; `rates.ts`'s own header names this component's copy as one of its two callers.
 * CI greps `client/` for bare `/ 11`, `/ 14` and `* 56`.
 *
 * ── Styling ────────────────────────────────────────────────────────────────
 * This file does not own a stylesheet. `reader-page.css` belongs to the reader frame and
 * `ui/reader.css` is lifted verbatim from books with a provenance header, so the layout here is
 * inline and every colour is a token — themes keep working, and nothing is added to a file another
 * session is editing. `.ab-btn` is reused because a second button style would be the third copy of
 * one control, which is the failure AGENTS.md records about books' page grid.
 */

import { useMemo, useState, type CSSProperties, type ReactElement } from "react";

import { CONTINUOUS_MODES, loadContinuousMode, saveContinuousMode, type ContinuousMode } from "../lib/continuous.ts";
import { PLAYBACK_RATES } from "../lib/listenPrefs.ts";
import { formatRemaining, nextSleepChoice, type SleepChoice, type SleepState } from "../audio/sleepTimer.ts";

import { estimateGpuMs } from "../../../shared/narration/rates.ts";
import type { PageState } from "../../../shared/narration/cue.ts";

/**
 * §5.7's `playbackRate`, widened to 0.75–2.5 with the fine steps around 1× where people actually
 * settle. Steps rather than a slider: a slider on a phone, on a control you touch twice a book, is a
 * drag target next to a page-turn gesture. Tapping cycles. The list lives in `lib/listenPrefs.ts`,
 * which validates what it remembers against the same numbers.
 */
export { PLAYBACK_RATES };

export interface PlaybackBarProps {
  /** The planner's answer for the page under the reader. Drives everything below. */
  state: PageState;
  playing: boolean;
  rate: number;
  /** Chapter-scoped, because §5.7's lock screen is chapter-scoped and two answers would diverge. */
  chapterTitle: string;
  /**
   * Milliseconds left **in this chapter**, measured where clips exist and estimated where they do
   * not (§4.6's hybrid axis). Null when unknown — and null renders nothing rather than "0:00", which
   * is LAW 9's habit applied to copy: an absent number is not a zero.
   */
  msLeftInChapter: number | null;
  /**
   * Characters this page still needs the GPU for. The honest input to "about 20 seconds": a page
   * whose clips are all cached from another book needs no GPU at all and must not claim 20 seconds.
   */
  pendingChars?: number | null;
  /** The estimate has already elapsed. One GPU, one lane — a queue behind you is normal. */
  overdue?: boolean;
  onPlayPause(): void;
  onRate(next: number): void;
  /** `POST /api/v1/pages/:pageId/narration/ensure`. Absent for a reader who may not spend GPU. */
  onNarrate?: (() => void) | null;
  /** §4.11's Retry, on a terminal failure. Absent for a reader who may not spend GPU. */
  onRetry?: (() => void) | null;
  /** The read-along's own complaint, shown here rather than over the text. */
  notice?: string | null;
  /** ±seconds on the chapter axis. Absent (a room) hides ⟲15 / 30⟳. */
  onSkip?: ((sec: number) => void) | null;
  /** The sleep timer, read live. Absent hides the control. */
  sleep?: (SleepState & { stopping: boolean }) | null;
  onSleep?: ((choice: SleepChoice | null) => void) | null;
  /** Open the full-screen Listen view. */
  onListen?: (() => void) | null;
}

const bar: CSSProperties = {
  flex: "0 0 auto",
  display: "flex",
  // Wraps rather than squeezes: at 390 px the transport is one row and the settings a second,
  // and every control keeps its 44 px target.
  flexWrap: "wrap",
  alignItems: "center",
  gap: "var(--space-2)",
  paddingInline: "var(--space-3)",
  minHeight: "var(--tap-min)",
  fontSize: "var(--text-xs)",
  color: "var(--read-ink-muted)",
};

const line: CSSProperties = {
  flex: "1 1 8rem",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontVariantNumeric: "tabular-nums",
};

export function PlaybackBar(props: PlaybackBarProps): ReactElement {
  const {
    state,
    playing,
    rate,
    chapterTitle,
    msLeftInChapter,
    pendingChars = null,
    overdue = false,
    onPlayPause,
    onRate,
    onNarrate = null,
    onRetry = null,
    notice = null,
    onSkip = null,
    sleep = null,
    onSleep = null,
    onListen = null,
  } = props;

  const ready = state === "ready";
  const warming = state === "queued" || state === "building";

  const bits = useMemo(() => {
    const out: string[] = [];
    if (chapterTitle) out.push(chapterTitle);
    const left = timeLeftLabel(msLeftInChapter);
    if (left) out.push(left);
    return out;
  }, [chapterTitle, msLeftInChapter]);

  return (
    <div style={bar} data-narration-state={state}>
      {ready && onSkip ? (
        <button type="button" className="ab-btn" onClick={() => onSkip(-15)} aria-label="Back 15 seconds" data-no-turn="">
          ⟲15
        </button>
      ) : null}
      {ready ? (
        <button
          type="button"
          className="ab-btn"
          onClick={onPlayPause}
          aria-label={playing ? "Pause the narration" : "Play the narration"}
          aria-pressed={playing}
          // `data-no-turn` keeps `usePageTurn` off this control: without it a tap here lands in the
          // left tap zone on a phone and turns the page as well as starting the voice.
          data-no-turn=""
        >
          {playing ? "Pause" : "Play"}
        </button>
      ) : null}
      {ready && onSkip ? (
        <button type="button" className="ab-btn" onClick={() => onSkip(30)} aria-label="Forward 30 seconds" data-no-turn="">
          30⟳
        </button>
      ) : null}

      <span style={line} aria-live="polite">
        {ready ? (
          bits.map((b, i) => (
            <span key={b + i}>
              {i > 0 ? (
                <span aria-hidden="true" style={{ opacity: 0.5, paddingInline: "0.45em" }}>
                  ·
                </span>
              ) : null}
              {b}
            </span>
          ))
        ) : (
          <StallCopy state={state} pendingChars={pendingChars} overdue={overdue} />
        )}
      </span>

      {notice ? (
        // The read-along's complaint lives here and not over the text. A banner across the page is
        // modal in everything but name, and the thing it is reporting — a highlight that could not
        // be placed — is by definition not urgent enough to interrupt reading.
        <span
          role="status"
          style={{ flex: "0 1 auto", minWidth: 0, color: "var(--warn)", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {notice}
        </span>
      ) : null}

      {ready ? (
        <button
          type="button"
          className="ab-btn"
          onClick={() => onRate(nextRate(rate))}
          aria-label={`Speed: ${formatRate(rate)}. Tap for the next speed.`}
          data-no-turn=""
        >
          {formatRate(rate)}
        </button>
      ) : null}

      {ready && sleep && onSleep ? (
        <button
          type="button"
          className="ab-btn"
          onClick={() => onSleep(nextSleepChoice(sleep))}
          aria-pressed={sleep.kind !== "off"}
          aria-label={`${sleepAria(sleep)} Tap for the next choice; past the last one it turns off.`}
          data-no-turn=""
        >
          {sleepShort(sleep)}
        </button>
      ) : null}

      {ready && onListen ? (
        <button type="button" className="ab-btn" onClick={onListen} aria-label="Open the full-screen Listen view" data-no-turn="">
          Listen
        </button>
      ) : null}

      {ready ? <ContinuousButton /> : null}

      {state === "missing" && onNarrate ? (
        <button type="button" className="ab-btn" onClick={onNarrate} data-no-turn="">
          Narrate this page
        </button>
      ) : null}

      {state === "failed" && onRetry ? (
        <button type="button" className="ab-btn" onClick={onRetry} data-no-turn="">
          Retry
        </button>
      ) : null}

      {warming ? (
        // Not a spinner and not an animation: a moving thing next to a fixed number invites the
        // reading "it is stuck" the moment the number stops matching the motion.
        <span aria-hidden="true" style={{ opacity: 0.6 }}>
          ⏳
        </span>
      ) : null}
    </div>
  );
}

/**
 * What happens at the end of the book, as one tap that cycles — the same shape as the speed control
 * beside it, for the same reason: a reader has no room for a settings pane and a two-tap menu for a
 * three-value choice is worse than a label that states the current answer.
 *
 * ⚠️ **It owns its own state rather than taking props.** The value lives in `localStorage` and is read
 * by `Read.tsx` at the moment the book ends, not held in React — so there is nothing to thread
 * through `Reader`, and no way for a stale prop to disagree with what the continuation will actually
 * do. `Room.tsx` composes its own `ReaderNarration` and would have been a second place to keep in
 * sync.
 */
function ContinuousButton(): ReactElement {
  const [mode, setMode] = useState<ContinuousMode>(() => loadContinuousMode());
  const here = CONTINUOUS_MODES.findIndex((m) => m.key === mode);
  const current = CONTINUOUS_MODES[here === -1 ? 0 : here]!;

  return (
    <button
      type="button"
      className="ab-btn"
      onClick={() => {
        const next = CONTINUOUS_MODES[(Math.max(here, 0) + 1) % CONTINUOUS_MODES.length]!;
        setMode(next.key);
        saveContinuousMode(next.key);
      }}
      aria-label={`At the end of the book: ${current.label}. ${current.hint} Tap for the next choice.`}
      data-no-turn=""
    >
      {current.key === "stop" ? "End: stop" : current.key === "repeat" ? "End: repeat" : "End: next"}
    </button>
  );
}

function StallCopy(props: {
  state: PageState;
  pendingChars: number | null;
  overdue: boolean;
}): ReactElement {
  const { state, pendingChars, overdue } = props;

  if (state === "failed") {
    // A refusal names what it refused and what would fix it (AGENTS.md, *Refusals*).
    return <>This page didn&rsquo;t narrate. Nothing was lost — retrying regenerates only what&rsquo;s missing.</>;
  }
  if (state === "missing") {
    return <>This page hasn&rsquo;t been narrated yet.</>;
  }
  if (overdue) {
    // One GPU, `AB_TTS_INFLIGHT=1`, and the interactive lane has a concurrency of one. A queue in
    // front of you is the normal, designed state, and saying so beats a number that has expired.
    return <>Still warming up — the GPU is finishing another page first.</>;
  }
  return <>Warming up — about {warmSeconds(pendingChars)} seconds.</>;
}

/**
 * The number in "warming up — about 20 seconds".
 *
 * `pendingChars` is what the plan says the GPU still owes for this page, so a page whose sentences
 * are all cache hits from another book reports the ~2 s the stitch costs rather than a fictional 20.
 * When the caller does not know, we fall back to a **typed** ignorance: `PAGE_TARGET_CHARS` is the
 * canonical page size (§2.3), so the fallback is "a whole page from cold" — the worst honest case —
 * rather than a literal somebody copied out of a design document once.
 */
function warmSeconds(pendingChars: number | null): number {
  const chars = pendingChars !== null && Number.isFinite(pendingChars) && pendingChars > 0 ? pendingChars : PAGE_CHARS;
  const secs = Math.round(estimateGpuMs(chars) / 1000);
  // Round to something a person would say. "about 27 seconds" is a lie told to two significant
  // figures; "about 25 seconds" is the same estimate without the false precision.
  return secs < 10 ? Math.max(1, secs) : Math.round(secs / 5) * 5;
}

/** §2.3's canonical page. Imported as a value would drag `shared/doc.ts` in for one integer. */
const PAGE_CHARS = 1500;

/** The bar's sleep button: short, because it shares a row with the transport. */
function sleepShort(s: SleepState & { stopping: boolean }): string {
  if (s.kind === "off") return "Sleep";
  if (s.kind === "chapter") return "Sleep: ch. end";
  return s.stopping ? "Sleep: now" : `Sleep ${formatRemaining(s.remainingMs)}`;
}

function sleepAria(s: SleepState & { stopping: boolean }): string {
  if (s.kind === "off") return "Sleep timer: off.";
  if (s.kind === "chapter") return "Sleep timer: stops at the end of this chapter.";
  return s.stopping ? "Sleep timer: stopping after this sentence." : `Sleep timer: ${formatRemaining(s.remainingMs)} left.`;
}

export function nextRate(rate: number): number {
  const i = PLAYBACK_RATES.findIndex((r) => Math.abs(r - rate) < 0.001);
  return PLAYBACK_RATES[(i + 1) % PLAYBACK_RATES.length] ?? 1;
}

export function formatRate(rate: number): string {
  const r = Math.round(rate * 100) / 100;
  return `${Number.isInteger(r) ? r.toFixed(0) : String(r)}×`;
}

/** "12 min left in chapter". Measured milliseconds, so no tilde — unlike the footer's estimate. */
function timeLeftLabel(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms <= 0) return "";
  const total = Math.round(ms / 1000);
  if (total < 60) return `${total}s left in chapter`;
  const mins = Math.round(total / 60);
  return `${mins} min left in chapter`;
}

export default PlaybackBar;
